import { useRef, type PointerEvent as ReactPointerEvent } from 'react'
import { useCanvasStore } from './canvas-store'

type Size = { w: number; h: number }

type ResizeOptions = {
  itemId: string
  // The item's last-committed size, used as the anchor for delta math.
  baseSize: Size
  // Called on pointer-up with the final size. Caller persists via tRPC. Not called if
  // the pointer barely moved.
  onCommit: (size: Size) => Promise<void> | void
  // Floor — keeps items from collapsing into a clickable point.
  minSize?: Size
}

// Pointer-driven resize for canvas items. Anchored to the top-left of the item (drag the
// bottom-right corner), so the position stays fixed and only the size grows / shrinks.
// Deltas are scaled by the canvas zoom so the handle tracks the cursor 1:1 on-screen.
export function useResizeInteraction({
  itemId,
  baseSize,
  onCommit,
  minSize = { w: 80, h: 60 },
}: ResizeOptions) {
  const beginResize = useCanvasStore((s) => s.beginResize)
  const updateResize = useCanvasStore((s) => s.updateResize)
  const endResize = useCanvasStore((s) => s.endResize)
  const stateRef = useRef<{
    startScreen: { x: number; y: number }
    startSize: Size
    moved: boolean
  } | null>(null)

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return
    e.stopPropagation()
    stateRef.current = {
      startScreen: { x: e.clientX, y: e.clientY },
      startSize: { ...baseSize },
      moved: false,
    }
    beginResize(itemId, baseSize)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const st = stateRef.current
    if (!st) return
    const scale = useCanvasStore.getState().scale
    const sdx = e.clientX - st.startScreen.x
    const sdy = e.clientY - st.startScreen.y
    if (!st.moved && Math.hypot(sdx, sdy) > 2) st.moved = true
    const next = {
      w: Math.max(minSize.w, st.startSize.w + sdx / scale),
      h: Math.max(minSize.h, st.startSize.h + sdy / scale),
    }
    updateResize(itemId, next)
  }

  const onPointerUp = (e: ReactPointerEvent<HTMLElement>) => {
    const st = stateRef.current
    if (!st) return
    e.currentTarget.releasePointerCapture(e.pointerId)
    const override = useCanvasStore.getState().resizeOverrides[itemId]
    stateRef.current = null
    if (!st.moved || !override) {
      endResize(itemId)
      return
    }
    const finalSize = override.size
    void Promise.resolve(onCommit(finalSize)).finally(() => endResize(itemId))
  }

  return { onPointerDown, onPointerMove, onPointerUp }
}
